// ============================================
// APPLICATIONS PAGE
// ============================================

import { createFileRoute, Link } from '@tanstack/react-router';
import { FileText, Ship, Clock, Loader2, X } from 'lucide-react';
import { useMyApplications, useWithdrawApplication } from '@/hooks';
import { formatRelativeTime, getStatusVariant } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/applications/')({
  component: ApplicationsPage,
});

function ApplicationsPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const { data: appsResponse, isLoading } = useMyApplications();
  const withdrawMutation = useWithdrawApplication();

  const applications: any[] = (appsResponse as any)?.data || appsResponse || [];

  const handleWithdraw = (id: string) => {
    if (confirm(t('panel:applications.withdrawConfirm'))) {
      withdrawMutation.mutate(id);
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-maritime-600" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('panel:applications.title')}</h1>
        <p className="text-gray-500 mt-1">{t('panel:applications.count', { count: applications.length })}</p>
      </div>

      {applications.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('panel:applications.noApplications')}</h3>
          <p className="text-gray-500 mb-4">{t('panel:applications.browseJobs')}</p>
          <Link to="/jobs" className="btn-primary">{t('panel:applications.viewJobs')}</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {applications.map((app: any) => (
            <div key={app.id} className="card p-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-maritime bg-maritime-100 flex items-center justify-center flex-shrink-0">
                  <Ship className="w-6 h-6 text-maritime-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Link to="/jobs/$slug" params={{ slug: app.job?.slug || '' }} className="font-medium text-gray-900 hover:text-maritime-600">
                        {app.job?.title}
                      </Link>
                      <p className="text-sm text-gray-500">{app.job?.company?.name}</p>
                    </div>
                    <span className={`badge badge-${getStatusVariant(app.status) === 'default' ? 'gray' : getStatusVariant(app.status)}`}>
                      {t(`enums:applicationStatus.${app.status}`, { defaultValue: app.status })}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                    <span>{t(`enums:vesselType.${app.job?.vesselType}`, { defaultValue: app.job?.vesselType })}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {t('panel:applications.appliedDate')}: {formatRelativeTime(app.createdAt)}
                    </span>
                  </div>
                  {app.notes && (
                    <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-2 rounded-maritime">
                      <strong>{t('panel:applications.note')}:</strong> {app.notes}
                    </p>
                  )}
                </div>
                {app.status === 'PENDING' && (
                  <button
                    onClick={() => handleWithdraw(app.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-maritime"
                    title={t('panel:applications.withdraw')}
                    disabled={withdrawMutation.isPending}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
